import { EnvironmentService } from 'app/shared/services/environment-service';
import { AccountService } from 'app/core/services/account-service';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { State } from 'app/shared/lib/state';
import { autoinject, camelCase, bindable } from 'aurelia-framework';
import { TickService, TickHandler, ITickEvent } from 'app/shared/services/event-services';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { Toaster } from 'app/shared/lib/toaster';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { INotifications } from 'app/shared/elements/ess-notifications';
import { getReferences, buildSnapshot } from 'app/shared/models/essette-types';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { SessionChangedEvent } from 'app/shared/lib/session/session-events';
import * as Core from 'app/core/models/essette-api';

@autoinject
export class EssUserNotificationsCustomElement {

    @bindable public maxCount: number = 100;

    protected count: number | null = null;
    protected pulse: boolean = false;
    protected notifications: Core.UserNotification[] = [];

    protected readonly state: State = new State();

    protected lastUpdate: number;
    protected readonly updateDuration: number;
    protected readonly updateNotifications: TickHandler;
    protected eventSubscription: Subscription | null = null;

    protected dropdown!: INotifications;

    constructor(
        protected readonly router: Router,
        protected readonly transitional: TransitionalManager,
        protected readonly toaster: Toaster,
        protected readonly i18n: I18N,
        protected readonly env: EnvironmentService,
        protected readonly events: EventAggregator,
        protected readonly ticker: TickService,
        protected readonly accountService: AccountService,
        protected readonly logger: AppLogger) {

        this.logger.source = this;
        this.logger.target = 'console';

        this.updateDuration = this.env.config.notifications.updateIntervalSeconds * 1000; // seconds to milliseconds
        this.lastUpdate = Date.now() - (0.75 * this.updateDuration); // don't do first update right away
        this.updateNotifications = (e: ITickEvent) => {
            if(e.time > this.lastUpdate + this.updateDuration) {
                if(!env.user.isLoggedIn) {
                    this.reset(false);
                }
                else if(!this.state.busy && env.isAppVisible()) {
                    this.loadNotifications();
                }
            }
        }
    }

    protected bind(): void {
        this.eventSubscription = this.events.subscribe(SessionChangedEvent,
            (e: SessionChangedEvent) => {
                if(e.hasUserLoggedOut) {
                    // if logged out (session expired), reset without loading
                    this.reset(false);
                }
                else if(e.hasUserContextChanged) {
                    // if user context has changed, force notifications load
                    this.reset(true);
                }
            });

        this.ticker.addEventListener('tick', this.updateNotifications);
        this.loadNotifications();
    }

    protected unbind(): void {
        this.ticker.removeEventListener('tick', this.updateNotifications);
        if(this.eventSubscription) {
            this.eventSubscription.dispose();
            this.eventSubscription = null
        }
    }

    protected handleNotificationsOpen(): void {
        this.pulse = false;
    }

    protected buildMessage(item: Core.UserNotification): string {
        const code: string = item.type && item.type.code || '';
        const fallback: string = (item.type && item.type.description) || item.$display || '';
        if(code) {
            const parts = code.split('.');
            const key: string[] = [];
            for(let index = 0; index < parts.length; index++) {
                const part = parts[index];
                if(index === 1) {
                    key.push(':');
                }
                else if(index > 1) {
                    key.push('.');
                }
                if(index === 0) {
                    key.push(part.toLowerCase());
                }
                else {
                    key.push(camelCase(part));
                }
                if(index === 1) {
                    key.push('.notifications');
                }
            }
            const options = {
                defaultValue: fallback,
                what: item.argument && item.argument.$display || null,
                whom: item.metadata && item.metadata.triggered && item.metadata.triggered.byUser && item.metadata.triggered.byUser.$display || null,
                queue: item.queue && item.queue.$display || null
            };
            const value = this.i18n.tr(key.join(''), options);
            if(value) {
                return value;
            }
        }
        return fallback;
    }

    protected viewAll(): void {
        this.dropdown?.close();
        this.router.navigate('core/my-notifications');
    }

    protected markAllRead(): void {
        this.dropdown?.close();
        this.markAllNotificationsRead();
    }

    protected handleNotificationClick({ target }: MouseEvent, item: Core.UserNotification) {
        const shouldNavigate = !(target as HTMLElement).matches('.ess-notification-dismiss');
        this.markNotificationRead(item, shouldNavigate);
        this.reset();
    }

    protected reset(load: boolean = true): void {
        this.notifications = [];
        this.count = null;
        this.pulse = false;
        this.lastUpdate = Date.now() - (0.75 * this.updateDuration); // don't do first update right away
        if(load) {
            this.loadNotifications();
        }
    }

    protected navigateTo(route: string): void {
        try {
            this.logger.debug(`Attempting to navigate to '${route}'.`);
            this.dropdown?.close();
            if(this.router.navigate(route)) {
                return;
            }
        }
        catch(error) {
            this.logger.error('Unable to navigate to Notification Target', error);
            this.toaster.warn().message('Unable to Navigate to Notification Target').show();
        }
    }

    protected async markAllNotificationsRead(): Promise<void> {
        try {
            this.state.beginSave();
            const notifications = getReferences(
                this.notifications.filter(notification => !notification.isRead),
                _ => ({ isRead: true }));
            if(!notifications) { return; }

            const response = await this.accountService.markNotificationsRead(this.env.user.id || '', notifications);
            if(response.isSuccessful) {
                let marked = 0;
                for(const notification of this.notifications) {
                    if(!notification.isRead) {
                        notification.isRead = true;
                        marked++;
                    }
                }
                // set update to zero so notifications will refresh ASAP
                this.lastUpdate = 0;
                this.count = Math.max(0, (this.count || 0) - marked);
                this.pulse = false;
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    protected async markNotificationRead(item: Core.UserNotification, navigate: boolean): Promise<void> {
        if(!item || !item.id) { return; }
        try {
            this.state.beginSave();
            const response = await this.accountService.markNotificationRead(item.id);
            if(response.isSuccessful) {
                item.isRead = true;
                this.lastUpdate = Date.now();
                this.count = Math.max(0, (this.count || 0) - 1);
                this.pulse = false;
                const result = response.result;
                if(navigate && result && typeof result.href === 'string') {
                    // append notification id to URL to force refresh
                    this.navigateTo(`${result.href}?notify=${item.id}`);
                }
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    protected async loadNotifications(): Promise<void> {
        try {
            this.state.beginLoad();
            if(!this.env.user.isLoggedIn) { return; }
            const response = await this.accountService.loadUnreadNotifications(100);
            if(response.isSuccessful) {
                const oldSnapshot = buildSnapshot(this.notifications.filter(notification => !notification.isRead));
                this.notifications = response.results;
                if(this.notifications.length > 0) {
                    const newSnapshot = buildSnapshot(response.results);
                    if(oldSnapshot !== newSnapshot) {
                        this.pulse = true;
                    }
                }
                else {
                    this.pulse = false;
                }
                this.count = this.notifications.length;
            }
        }
        catch(error) {
            this.logger.error('Error Loading Notifications', error);
            this.count = null;
        }
        finally {
            this.lastUpdate = Date.now();
            this.state.endLoad();
        }
    }

}

